import React, { useState, useEffect } from 'react';
import * as api from '../services/api';
import { useSort, usePager, SortableTh } from '../components/TableTools';

interface SyncLog {
  id: string;
  user_name: string;
  device_id: string;
  sync_started_at: string;
  sync_status: string;
  sync_result: string;
  records_sent: number;
  records_succeeded: number;
  records_failed: number;
  records_conflict: number;
  communication_type: string;
}

const STATUS_BADGE: Record<string, { background: string; color: string; label: string }> = {
  synced:  { background: '#dcfce7', color: '#16a34a', label: '完了' },
  syncing: { background: '#fef3c7', color: '#d97706', label: '同期中' },
  pending: { background: '#dbeafe', color: '#2563eb', label: '保留' },
  failed:  { background: '#fee2e2', color: '#dc2626', label: '失敗' },
};
const RESULT_BADGE: Record<string, { background: string; color: string; label: string }> = {
  success:         { background: '#dcfce7', color: '#16a34a', label: '成功' },
  partial_success: { background: '#fef3c7', color: '#d97706', label: '部分成功' },
  error:           { background: '#fee2e2', color: '#dc2626', label: 'エラー' },
};
const FALLBACK_BADGE = { background: '#f1f5f9', color: '#64748b', label: '-' };

const SyncLogsPage: React.FC = () => {
  const [logs, setLogs]           = useState<SyncLog[]>([]);
  const [loading, setLoading]     = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await api.get<any>('/sync-logs');
      const raw = res?.logs ?? res?.data ?? [];
      setLogs(raw.map((l: any) => ({
        id:                 String(l.id ?? ''),
        user_name:          String(l.user_name ?? ''),
        device_id:          String(l.device_id ?? ''),
        sync_started_at:    String(l.sync_started_at ?? l.created_at ?? ''),
        sync_status:        String(l.sync_status ?? l.status ?? 'pending'),
        sync_result:        String(l.sync_result ?? l.result ?? ''),
        records_sent:       Number(l.records_sent      ?? 0),
        records_succeeded:  Number(l.records_succeeded ?? 0),
        records_failed:     Number(l.records_failed    ?? 0),
        records_conflict:   Number(l.records_conflict  ?? 0),
        communication_type: String(l.communication_type ?? l.comm_type ?? ''),
      })));
    } catch {
      setLogs([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, []);

  const filtered = statusFilter ? logs.filter(l => l.sync_status === statusFilter) : logs;

  // 絞り込み → ソート → ページング（デフォルトは開始日時の新しい順）
  const sort = useSort<SyncLog>(filtered, {
    user_name:          l => l.user_name,
    device_id:          l => l.device_id,
    sync_started_at:    l => l.sync_started_at,
    communication_type: l => l.communication_type,
    sync_status:        l => (STATUS_BADGE[l.sync_status] ?? FALLBACK_BADGE).label,
    sync_result:        l => (RESULT_BADGE[l.sync_result] ?? FALLBACK_BADGE).label,
    records_sent:       l => l.records_sent,
    records_succeeded:  l => l.records_succeeded,
    records_failed:     l => l.records_failed,
    records_conflict:   l => l.records_conflict,
  }, 'sync_started_at', 'desc');
  const pager = usePager(sort.sorted, { noun: '件' });

  const counts = {
    synced:  logs.filter(l => l.sync_status === 'synced').length,
    syncing: logs.filter(l => l.sync_status === 'syncing').length,
    failed:  logs.filter(l => l.sync_status === 'failed').length,
  };

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <div>
          <h1 className="page-title">同期ログ</h1>
          <p style={{ margin: '3px 0 0', color: 'var(--text-secondary)', fontSize: 13 }}>端末データの同期状況を確認します</p>
        </div>
        <button onClick={fetchLogs} className="btn btn-secondary btn-sm">更新</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { key: 'synced',  label: '同期完了', color: '#16a34a' },
          { key: 'syncing', label: '同期中',   color: '#d97706' },
          { key: 'failed',  label: '失敗',     color: '#dc2626' },
        ].map(c => (
          <div key={c.key} style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderLeft: '4px solid ' + c.color, cursor: 'pointer', outline: statusFilter === c.key ? '2px solid ' + c.color : 'none' }}
            onClick={() => setStatusFilter(statusFilter === c.key ? '' : c.key)}>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: c.color }}>{counts[c.key as keyof typeof counts] ?? 0}</div>
          </div>
        ))}
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: '14px 20px', marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', gap: 12, alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          状態:
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ border: '1px solid #ccc', borderRadius: 6, padding: '5px 10px', fontSize: 13 }}>
            <option value="">全状態</option>
            {Object.entries(STATUS_BADGE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
        <span style={{ marginLeft: 'auto', color: '#999', fontSize: 12 }}>{filtered.length}件表示</span>
      </div>

      {loading ? (
        <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', textAlign: 'center', padding: 40, color: '#94a3b8' }}>読み込み中...</div>
      ) : (
        <>
        {pager.renderBar('top')}
        <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f5f7ff' }}>
                <SortableTh label="乗組員" k="user_name" sort={sort} />
                <SortableTh label="端末ID" k="device_id" sort={sort} />
                <SortableTh label="開始日時" k="sync_started_at" sort={sort} />
                <SortableTh label="通信" k="communication_type" sort={sort} />
                <SortableTh label="状態" k="sync_status" sort={sort} />
                <SortableTh label="結果" k="sync_result" sort={sort} />
                <SortableTh label="送信" k="records_sent" sort={sort} />
                <SortableTh label="成功" k="records_succeeded" sort={sort} />
                <SortableTh label="失敗" k="records_failed" sort={sort} />
                <SortableTh label="競合" k="records_conflict" sort={sort} />
              </tr>
            </thead>
            <tbody>
              {pager.pageRows.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#999' }}>データがありません</td></tr>
              ) : pager.pageRows.map((l, i) => {
                const sb = STATUS_BADGE[l.sync_status]  ?? FALLBACK_BADGE;
                const rb = RESULT_BADGE[l.sync_result]  ?? FALLBACK_BADGE;
                return (
                  <tr key={l.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 600 }}>{l.user_name}</td>
                    <td style={{ padding: '12px 14px', fontSize: 11, fontFamily: 'monospace', color: '#64748b' }}>{l.device_id}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, color: '#64748b' }}>{l.sync_started_at.slice(0, 16)}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12 }}>{l.communication_type || '-'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: sb.background, color: sb.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{sb.label}</span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: rb.background, color: rb.color, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{rb.label}</span>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 12, textAlign: 'right' }}>{l.records_sent}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{l.records_succeeded}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, textAlign: 'right', color: l.records_failed > 0 ? '#dc2626' : '#94a3b8', fontWeight: l.records_failed > 0 ? 700 : 400 }}>{l.records_failed}</td>
                    <td style={{ padding: '12px 14px', fontSize: 12, textAlign: 'right', color: l.records_conflict > 0 ? '#d97706' : '#94a3b8' }}>{l.records_conflict}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pager.renderBar('bottom')}
        </>
      )}
    </div>
  );
};

export default SyncLogsPage;
